/**
 * @file ShopLayout.tsx
 * @description Каркас кабинета магазина на Commora Console (phase08.5;
 * phase19.31 шаг О3 — SC14–SC19, SC24, SC30, SC31, SC44).
 *
 * Тот же `AppShell`, что у кабинета платформы (`pinSidebarAt="lg"`,
 * высокая шапка, выровненная по колонке содержимого, блок пользователя
 * внизу панели), но:
 *  - палитра — тема МАГАЗИНА (`useShopCabinetTheme`: кэш → сервер →
 *    запасная «Commora»), правила и размеры — общие (SC1–SC3);
 *  - в шапке панели — магазин на виду и переключатель (`ShopIdentity`),
 *    на телефоне и планшете монограмма стоит ещё и в верхней панели (SC19);
 *  - меню — реестр `SHOP_NAV` по правам (`buildShopNav`): группы, поиск,
 *    счётчики одним запросом (SC14–SC18);
 *  - экран кладовщика (SC30, SC44): только «Склад», плотность «склад»
 *    (56px до 1024) на <html>; главная магазина ведёт в «Обзор склада»;
 *  - часовой пояс магазина для подписей времени (SC31);
 *  - заголовок вкладки «<раздел> — <магазин>» (SC19);
 *  - полоса входа под сотрудником закреплена над шапкой (SC24), под ней —
 *    плашка «Сайт закрыт для покупателей» (phase19.16, SB138): опрос
 *    состояния паузы один на вкладку — здесь (`useSiteAccess` в режиме `poll`);
 *    рядом — красная плашка «Сайт закрыт от поисковиков» (Р3в, SB145, SB132:
 *    другая причина, своя полоса), значение — `useSearchIndexingClosed`;
 *  - окно «Сообщения» и кнопка чата (SC64) — над `<Outlet/>`: переход
 *    между страницами окно не пересоздаёт; провайдер пересоздаётся при смене
 *    магазина и начале/конце входа под сотрудником (`key`). В редакторе
 *    страницы сайта угол занят панелью — кнопка значком в верхней панели.
 * Кабинет вкладки по адресу выбирает `RequireContextType` (выше по дереву):
 * сюда приходит уже контекст магазина из адреса.
 */

import { useMemo } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router";

import { useAuth } from "../auth/hooks";
import { AppHeader } from "../components/AppHeader";
import { ChatLauncher } from "../components/chat/ChatLauncher";
import { ChatWindow } from "../components/chat/ChatWindow";
import { ChatWindowProvider } from "../components/chat/ChatWindowProvider";
import { chatLauncherPlacement } from "../components/chat/chat-launcher-placement";
import { ImpersonationBanner } from "../components/ImpersonationBanner";
import { SiteClosedBanner } from "../components/site-access/SiteClosedBanner";
import { SearchClosedBanner } from "../components/site-seo/SearchClosedBanner";
import { SidebarUserMenu } from "../components/SidebarUserMenu";
import { ShopIdentity } from "../components/shop-nav/ShopIdentity";
import { ShopMonogram } from "../components/shop-nav/ShopMonogram";
import { ShopSidebarNav } from "../components/shop-nav/ShopSidebarNav";
import { AppShell } from "../components/ui/AppShell";
import { CabinetHeaderSlotsContext, useCabinetHeaderSlotState } from "../components/ui/cabinet-header-slots";
import { useSiteAccess } from "../hooks/site-access/useSiteAccess";
import { useCabinetDensity } from "../hooks/useCabinetDensity";
import { useShopCabinetTheme } from "../hooks/useCabinetTheme";
import { useNavCounters } from "../hooks/useNavCounters";
import { buildShopNav, findSectionItem, isWarehouseMode, shopRelativePath } from "../lib/shop-nav";
import { activeSiteClosure } from "../lib/site-access/site-access-view";
import { setShopTimeZone } from "../lib/shop-time";
import { useSearchIndexingClosed } from "../lib/site-seo/seo-settings-store";

export function ShopLayout() {
  const { me } = useAuth();
  const ctx = me?.activeContext ?? null;
  const shop = ctx?.contextType === "SHOP" ? ctx.shop : null;
  const { slug } = useParams<{ slug: string }>();
  const { pathname } = useLocation();

  // Часовой пояс магазина — до отрисовки страниц: функции форматирования
  // времени читают его синхронно (SC31). Повторная установка безвредна.
  setShopTimeZone(shop?.timeZone);

  useShopCabinetTheme(shop);
  const warehouse = isWarehouseMode(ctx);
  useCabinetDensity(warehouse);
  const permissions = ctx?.role.permissions;
  const nav = useMemo(() => buildShopNav(permissions ?? [], warehouse ? "warehouse" : "full"), [permissions, warehouse]);
  const counters = useNavCounters(!!shop, ctx?.id ?? null);
  // Сайт закрыт для покупателей (SB138): опрос один на вкладку — здесь, плашка над шапкой.
  const siteAccess = useSiteAccess(shop ? (ctx?.id ?? null) : null, "poll");
  const siteClosed = activeSiteClosure(siteAccess.state, siteAccess.serverNow()) !== null;
  // Сайт закрыт от поисковиков (SB145): видят сотрудники с правом на сайт (контент или настройки).
  const canSeeSeo = !!permissions?.some((p) => p === "SHOP_MANAGE_SETTINGS" || p === "SHOP_MANAGE_CONTENT") && !warehouse;
  const searchClosed = useSearchIndexingClosed(!!shop && canSeeSeo);
  const relativePath = shop ? shopRelativePath(pathname, shop.slug) : "";
  const section = findSectionItem(nav, relativePath);

  // Вкладки разных магазинов различаются заголовком: «Заказы — Мёд Алтая».
  // Это запасной вариант: страница с `CabinetPageHeader` объявляет своё
  // название, и вкладка становится «Обзор склада — Мёд Алтая» (О4).
  const tabTitle = shop ? (section && section.path !== "" ? `${section.label} — ${shop.displayName}` : shop.displayName) : null;
  // Заголовок страницы «сжимается» вместе со шторкой меню — до 1024 (О4).
  const slots = useCabinetHeaderSlotState(shop?.displayName ?? "Commora", {
    compactBelow: "lg",
    fallbackTitle: tabTitle,
  });

  const chatEnabled = !!permissions?.includes("SHOP_USE_CUSTOMER_CHAT") && !warehouse;
  const chatPlacement = chatLauncherPlacement(relativePath);

  if (!me || !ctx || !shop || shop.slug !== slug) {
    return <Navigate to="/select-context" replace />;
  }
  // Кладовщику главная магазина (сводка продаж) не показывается — обзор склада (W11).
  if (warehouse && relativePath === "") {
    return <Navigate to={`/shop/${shop.slug}/wms`} replace />;
  }

  return (
    <CabinetHeaderSlotsContext.Provider value={slots.value}>
      <ChatWindowProvider
        key={`${shop.id}:${me.impersonation?.sessionId ?? "self"}`}
        enabled={chatEnabled}
        shopId={shop.id}
        slug={shop.slug}
        userId={me.user.id}
        readOnly={!!me.impersonation}
        canDispatch={!!permissions?.includes("SHOP_DISPATCH_CUSTOMER_CHAT")}
        canOpenOrders={!!permissions?.includes("SHOP_VIEW_ORDERS")}
        canOpenCustomers={!!permissions?.includes("SHOP_VIEW_CUSTOMERS")}
      >
        <AppShell
          pinSidebarAt="lg"
          headerSize="tall"
          banner={
            me.impersonation || siteClosed || searchClosed ? (
              <>
                {me.impersonation ? <ImpersonationBanner /> : null}
                {siteClosed ? <SiteClosedBanner access={siteAccess} /> : null}
                {searchClosed ? <SearchClosedBanner slug={shop.slug} /> : null}
              </>
            ) : undefined
          }
        >
          <AppShell.Sidebar>
            {/* Шапка панели (магазин + поиск по меню) и список пунктов. */}
            <ShopSidebarNav
              nav={nav}
              slug={shop.slug}
              counters={counters}
              identity={<ShopIdentity shop={shop} nav={nav} />}
            />

            <AppShell.SidebarFooter flush>
              <SidebarUserMenu />
            </AppShell.SidebarFooter>
          </AppShell.Sidebar>

          <AppShell.Header aligned>
            <AppHeader
              start={
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {/* Магазин на виду и там, где панель в шторке (до 1024). */}
                  <ShopMonogram shopId={shop.id} name={shop.displayName} size={24} className="lg:hidden" />
                  <div ref={slots.setStartEl} className="min-w-0 flex-1" />
                </div>
              }
              end={
                <div className="flex items-center gap-1">
                  <div ref={slots.setEndEl} className="flex items-center" />
                  {chatPlacement === "header" ? <ChatLauncher variant="header" /> : null}
                </div>
              }
              hideUser
            />
          </AppShell.Header>

          <AppShell.Content>
            {/* Отступ под общую нижнюю полосу (до 1024) — только когда она есть;
                6rem — кнопка 56px у кладовщика, подпись над ней и поля полосы.
                Кнопка чата над полосой — ещё её высота с зазором
                (--chat-fab-over-bar): низ формы не прячется под ней (QA-5 D5). */}
            <div
              className={
                chatEnabled && chatPlacement === "fab"
                  ? "pb-[var(--chat-fab-reserve)] max-lg:has-[[data-cabinet-bottom-action]]:pb-[calc(6rem+var(--chat-fab-over-bar)+env(safe-area-inset-bottom))]"
                  : "max-lg:has-[[data-cabinet-bottom-action]]:pb-[calc(6rem+env(safe-area-inset-bottom))]"
              }
            >
              <ChatWindow />
              <Outlet />
              <div ref={slots.setBottomEl} />
            </div>
            {chatPlacement === "fab" ? <ChatLauncher /> : null}
          </AppShell.Content>
        </AppShell>
      </ChatWindowProvider>
    </CabinetHeaderSlotsContext.Provider>
  );
}
